import type { RemoteResourceProvider } from '../providers/remote-resource-provider.ts'
import type { PermissionService } from '../permissions/permission-service.ts'
import type { ProviderPermissions } from '../permissions/notification-permission-service.ts'
import type { PromptDispatchMessage } from '../types/prompt-dispatch-message.ts'
import type { EventTracker } from '@core/events/event-manager.ts'
import type { UserStore } from '../storage/user-storage-manager.ts'
import type { PromptLifecycleCallbacks } from '../callbacks/prompt-lifecycle-callbacks.ts'
import { DisplayCoordinatorDelegate, PromptDisplayCoordinator } from '../types/display-coordinator.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import {
    getPromptCondition,
    HtmlPrompt,
    isAutoShowPrompt,
    isPageViewsPromptCondition,
    isScrollDepthPromptCondition,
    isSessionStartPrompt,
    isSessionTimeIntervalPromptCondition,
    type Prompt,
    PromptConditionType,
    type PromptDisplayConditions,
    type PromptGroup,
    type PromptPageConditions,
    PromptPageViewsCondition,
    PromptScrollDepthCondition,
    PromptSessionTimeIntervalCondition,
} from '../types/prompts.ts'
import { PromptView } from './prompt-view.ts'
import { PromptActionType } from '../types/prompt-action-type.ts'
import { SubscriberStatus } from '../enums/subscriber-status.ts'
import { Env } from '../env.ts'
import { PromptStyle } from '../enums/prompt-style.ts'
import { PromptFailedToShowReason } from '../enums/prompt-failed-to-show-reason.ts'
import { Result } from '@core/models/result.ts'
import { lazy } from '@core/decorators/lazy.ts'
import { DevicePlacement } from '@core/types/device-placement.ts'
import { SettingsStore } from '../storage/settings-storage-manager.ts'
import { timeIntervalFromNow } from '@core/utils/time.ts'
import { SessionManager } from '../session/session-manager.ts'
import { Timer } from '@core/timer/timer.ts'
import { FrequencyCap } from '@core/types/frequency-cap.ts'
import { DomainConfig } from '@core/types/domain-config.ts'
import { TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { Globals } from '../globals.ts'
import { DebugController } from '../debug/debug-controller.ts'
import { DebugEventReason } from '../enums/debug-event-reason.ts'
import { CustomPromptView } from './custom-prompt-view.ts'
import { PushSdkConfig } from '../types/push-sdk-config.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { testSafeCaseInsensitivePattern } from '../utils/safe-dynamic-pattern.ts'
import { ScrollDepthObserver } from '../processors/scroll-depth-observer.ts'
/** @external */
export type ShowPromptOptions = {
    skipEligibilityCheck?: boolean
}

/** @internal */
export type PromptMatch = {
    group: PromptGroup
    prompt: Prompt
    displayCoordinator?: PromptDisplayCoordinator
}

/** @internal */
export class PromptsController implements DisplayCoordinatorDelegate {
    private readonly logger: Logger

    /**
     * Represents when external resources are ready
     * and the controller is now able to start evaluations.
     */
    private isDisplayEvaluationReady: boolean = false

    /**
     * Represents if Prompt resources have completed loading.
     */
    private isPromptLoadingCompleted: boolean = false

    protected readonly timeInSessionTimer: Timer
    protected manualShowRequestIsActive: boolean = false

    /**
     * When true, all prompting is suppressed: automatic timer-driven prompts and
     * explicit showPrompt requests alike. Partner-controlled at runtime; in-memory
     * only, so it survives SPA navigation and resets on full page reload.
     */
    private isPromptingSuppressed: boolean = false

    private promptViewInitializedCallbacks: Array<(DisplayCoordinator: PromptDisplayCoordinator) => void> = []

    private allPrompts: PromptMatch[] = []
    // NATIVE prompts never get a displayCoordinator (see `_buildMatchedPrompt`), so this can no
    // longer be `Required<PromptMatch>` — every other style is still guaranteed one once matched.
    private matchedPrompt: PromptMatch | undefined
    private activeView: PromptDisplayCoordinator | undefined

    /**
     * The NATIVE prompt whose browser permission dialog is currently open, tracked independently
     * of {@link activeView}. NATIVE prompts never get a {@link PromptDisplayCoordinator} (there is
     * nothing to `_display()`/`_hide()` — the browser owns that UI), so `activeView` alone cannot
     * signal "a prompt is currently showing" for them. Set once, in {@link _showMatchedPrompt}, and
     * never cleared: once the browser has shown its permission dialog for a domain it will not
     * show it again, so — mirroring how {@link activeView} previously persisted for the rest of the
     * page session once a (never-displayed) `PromptView` stood in for a shown NATIVE prompt — this
     * is intentionally permanent for the page lifetime, not reset when the dialog resolves.
     */
    private nativePromptCurrentlyShowing: Prompt | undefined

    private lastPageViewed: string | null = null

    public constructor(
        private readonly pushSdkConfig: PushSdkConfig,
        private readonly domain: DomainConfig,
        private readonly promptsProvider: RemoteResourceProvider<PromptGroup[]>,
        private readonly notificationPermissionService: PermissionService<ProviderPermissions>,
        private readonly userStore: UserStore,
        private readonly settingsStore: SettingsStore,
        private readonly sessionManager: SessionManager,
        private readonly scrollDepthObserver: ScrollDepthObserver,
        private readonly eventManager: EventTracker,
        private readonly debugController: DebugController,
        private readonly promptLifecycleCallbacks?: PromptLifecycleCallbacks,
        logger?: Logger,
    ) {
        this.lastPageViewed = this._currentHref
        this.timeInSessionTimer = new Timer('prompts_tis_timer', this._handleSessionTimerDidAdvance.bind(this), 1)
        this.logger = logger ?? TaggedLogger._getInstance('PromptsController')

        this._load()
    }

    protected _load() {
        // Start prompt loading as soon as possible to account
        // for custom prompt flows.
        this._loadPrompts()
    }

    @lazy protected get _devicePlacement(): DevicePlacement {
        return Env._devicePlacement
    }

    @lazy protected get _isTwoStepPromptsRequired(): boolean {
        return Env._requires2Step
    }

    protected get _currentHref() {
        return Env._location.href
    }

    protected get _document() {
        return Env._document
    }

    protected get _currentPageMetaKeywords(): string[] {
        const querySelectors: string[] = [
            "meta[name='keywords']",
            "meta[name='parsely-tags']",
            "meta[name='sailthru.tags']",
        ]

        try {
            return Array.from(
                new Set(
                    Array.from(this._document.querySelectorAll(querySelectors.join(',')))
                        .filter((meta): meta is HTMLMetaElement => meta instanceof HTMLMetaElement && !!meta.content)
                        .flatMap((meta) => meta.content.split(','))
                        .map((kw) => kw.toString().trim().toLowerCase())
                        .filter((kw) => kw),
                ),
            )
        } catch (e) {}

        return []
    }

    protected get _isReady(): boolean {
        return this.isPromptLoadingCompleted && this.isDisplayEvaluationReady
    }

    /**
     * The prompt currently occupying the screen, whether that is a non-NATIVE style mid-display
     * (via {@link activeView}) or a NATIVE prompt whose browser permission dialog is open (via
     * {@link nativePromptCurrentlyShowing}). Guards that need to know "is something showing right
     * now" — re-show suppression, SPA re-attempt gating — read this rather than {@link activeView}
     * directly, since NATIVE prompts never populate {@link activeView}.
     */
    protected get _currentlyShowingPrompt(): Prompt | undefined {
        return this.activeView?.prompt ?? this.nativePromptCurrentlyShowing
    }

    /**
     * Sets callback to be run against any new Prompt Views.
     */
    protected setPromptViewInitializedCallbacks(callback: (DisplayCoordinator: PromptDisplayCoordinator) => void) {
        if (this.activeView) {
            callback(this.activeView)
        } else {
            this.promptViewInitializedCallbacks.push(callback)
        }
    }

    /**
     * Signals that external resources have been loaded
     * and the controller can now attempt to find a matched
     * prompt and start the internal session timer.
     */
    public _setIsWebPushSupported(supported: Result<true, PNException>): void {
        if (supported.isErr()) {
            this.debugController._debugPromptNotShown(
                DebugEventReason.WEB_PUSH_UNSUPPORTED,
                supported.getErr().details ?? 'unknown',
            )
        }

        this.setPromptViewInitializedCallbacks((view) => {
            if (view instanceof CustomPromptView) {
                supported ? view._setSupportedClassName() : view._setUnsupportedClassName()
            }
        })
    }

    /**
     * Signals that external resources have been loaded
     * and the controller can now attempt to find a matched
     * prompt and start the internal session timer.
     */
    public async _startDisplayEvaluations() {
        this.isDisplayEvaluationReady = true

        if (this._isReady && !this.matchedPrompt && !this.isPromptingSuppressed) {
            await this._startMatchedPromptSelection()
        }

        this.timeInSessionTimer._start()

        if (this.isPromptingSuppressed) {
            this._disable()
        }
    }

    /**
     * Enables the controller's internal session timer.
     */
    public get _isEnabled(): boolean {
        return this.timeInSessionTimer.isStarted && !this.timeInSessionTimer.isPaused
    }

    /**
     * Enables the controller's internal session timer.
     */
    public _enable() {
        this.logger.verbose('Enabling session timer')
        this.timeInSessionTimer._unpause()
    }

    /**
     * Disables the controller's internal session timer.
     */
    public _disable() {
        this.logger.verbose('Disabling session timer')
        this.timeInSessionTimer._pause()
    }

    /**
     * Returns whether the partner has suppressed prompting at runtime.
     */
    public _getIsPromptingSuppressed(): boolean {
        return this.isPromptingSuppressed
    }

    /**
     * Suppresses all prompting until {@link _allowPrompts} is called. Blocks both
     * automatic and manual prompt display, and takes down a prompt that is already on screen
     * without recording it as dismissed. A NATIVE permission dialog is owned by the browser and
     * cannot be closed from here, so only dialogs not yet requested are prevented. Idempotent.
     */
    public _suppressPrompts(): void {
        if (this.isPromptingSuppressed) {
            return
        }

        this.logger.verbose('Suppressing prompts')
        this.isPromptingSuppressed = true
        this._disable()
        this._hideActiveViewWithoutDismissal()
    }

    /**
     * Removes the on-screen prompt, if any, and releases it. Unlike a visitor dismissal this
     * neither tracks a dismissal event nor changes the subscriber status, so the prompt remains
     * eligible to show again once prompting is allowed.
     */
    private _hideActiveViewWithoutDismissal(): void {
        const activeView = this.activeView
        if (!activeView) {
            return
        }

        this.logger.verbose('Hiding the active prompt because prompting is suppressed')
        this.activeView = undefined
        activeView._hide()
    }

    /**
     * Whether a prompt that is about to be rendered or requested must be abandoned because the
     * partner suppressed prompting after the display decision was made. Suppression can land at
     * any await between the decision and the render, so this is checked immediately before the
     * prompt reaches the page or the browser.
     */
    private _abandonDisplayBecausePromptingIsSuppressed(): boolean {
        if (!this.isPromptingSuppressed) {
            return false
        }

        this.logger.verbose('Prompt not shown - prompting was suppressed while it was being prepared')
        this.debugController._debugPromptNotShown(DebugEventReason.PROMPTING_SUPPRESSED)
        return true
    }

    /**
     * Re-allows prompting after {@link _suppressPrompts}. Re-runs prompt selection so
     * a prompt that became due while suppressed can display. Idempotent.
     */
    public _allowPrompts(): void {
        if (!this.isPromptingSuppressed) {
            return
        }

        this.logger.verbose('Allowing prompts')
        this.isPromptingSuppressed = false
        this._enable()

        if (this._isReady) {
            this._startMatchedPromptSelection().then()
        }
    }

    /**
     * Fetches prompts and flattens to a {@link PromptMatch} list with
     * each {@link Prompt} associated with its respective {@link PromptGroup}.
     *
     * <b>NOTE:</b> [displayCoordinator]{@link PromptMatch.displayCoordinator}
     * is not assigned during the initial load process.
     */
    public async _loadPrompts() {
        this.logger.verbose('Loading prompts')
        const promptsRes = await this.promptsProvider._get()

        if (promptsRes.isErr()) {
            this.logger.warn('No prompts available', promptsRes.getErr())
            this.debugController._debugPromptNotShown(DebugEventReason.PROMPTS_LOAD_FAILED)
            return
        }

        const promptGroups = promptsRes.unwrap()
        if (!promptGroups.length) {
            this.logger.warn('No prompts available')
            this.debugController._debugPromptNotShown(DebugEventReason.NO_ELIGIBLE_PROMPTS)
            return
        }

        this.allPrompts = promptGroups.flatMap((group) => group.prompts.map((prompt) => ({ prompt, group })))

        await this._handlePromptsLoaded()
    }

    protected async _handlePromptsLoaded() {
        this.logger.verbose('Prompts loaded')
        this.isPromptLoadingCompleted = true

        if (this._isReady && !this.isPromptingSuppressed) {
            await this._startMatchedPromptSelection()
        }
    }

    /**
     * TimerTask to determine when to show matched prompt if not already the active view.
     *
     * <b>SPA logic:</b> If no prior match and page has changed then prompt selection is restarted.
     */
    protected async _handleSessionTimerDidAdvance(): Promise<void> {
        if (!this._isReady) {
            return
        }

        let matchedPrompt = this.matchedPrompt

        // SPA logic
        if (!matchedPrompt && this._currentHref !== this.lastPageViewed) {
            this.lastPageViewed = this._currentHref
            await this._startMatchedPromptSelection()

            // restart timer to ensure global / user conditions evaluation may have changed in meantime
            this._enable()
        }

        // no prompt to show on this page - should not disable time as SPA page can change (no refresh)
        if (!matchedPrompt) {
            return
        }

        if (
            !this._currentlyShowingPrompt &&
            (isAutoShowPrompt(matchedPrompt.prompt) || this.manualShowRequestIsActive)
        ) {
            await this._attemptToShowPrompt(matchedPrompt)
        } else {
            this._disable()
        }
    }

    /**
     * Updates local {@link matchedPrompt} property and seeds to session cache.
     */
    protected async _setLastMatchedPrompt(match: PromptMatch) {
        if (this.matchedPrompt?.prompt.id === match.prompt.id) {
            // noop if already locally cached
            return
        }

        const name = match.prompt.name ?? match.group.name
        this.logger.verbose(`Setting matched Prompt: ${match.prompt.id} (${match.prompt.style}) ${name}`)

        this.matchedPrompt = await this._buildMatchedPrompt(match)

        // Seed last matched id to background loop
        this.settingsStore._setLastMatchedPromptId(match.prompt.id).then()

        // set data to be passed on all permission events associated
        // with the current prompt flow.
        this.notificationPermissionService._setAdditionalPermissionEventData({
            prompt_id: match.prompt.id,
        })
    }

    /**
     * Clears the {@link matchedPrompt} property and associated session cached
     */
    protected async _clearLastMatchedPrompt() {
        this.matchedPrompt = undefined
        await this.settingsStore._setLastMatchedPromptId(null)
    }

    /**
     * Used for public show_prompt pass-through, does not evaluate Prompt conditions.
     */
    public async _showPrompt(id?: number, options?: ShowPromptOptions): Promise<void> {
        if (this.isPromptingSuppressed) {
            this.logger.info('Show prompt request ignored - prompting is suppressed')
            this.debugController._debugPromptNotShown(DebugEventReason.PROMPTING_SUPPRESSED)
            return
        }

        this.manualShowRequestIsActive = true

        let match: PromptMatch | null = null
        if (id === undefined) {
            this.logger.verbose('Show prompt invoked')

            // attempt to use locally cached prompt or
            // fallback to re-selection process
            if (!this.matchedPrompt) {
                await this._startMatchedPromptSelection()
            }
            match = this.matchedPrompt ?? null
        } else {
            this.logger.verbose(`Show prompt invoked for ${id}`)

            if (this.matchedPrompt?.prompt.id === id) {
                // If request prompt id is current match skip to conditions check
                match = this.matchedPrompt
            } else {
                // Attempt to find requested prompt in loaded prompts
                const requested = this.allPrompts.find(({ prompt }) => prompt.id === id)
                if (requested) {
                    match = await this._buildMatchedPrompt(requested)
                }
            }
        }

        if (!match) {
            this.logger.warn(`Prompt${id === undefined ? '' : ` ${id}`} not found`)
            this.promptLifecycleCallbacks?.onPushSDKFailedToShowPrompt?.(
                id ?? null,
                PromptFailedToShowReason.NOT_AVAILABLE,
            )

            return
        }

        // run conditions check on found prompt match
        const canShow = await this._hasMetGlobalAndUserConditions(match.prompt, options)
        if (match.prompt.style === PromptStyle.PUBLISHER_CUSTOM) {
            // Custom Prompts require classNames to be set for all states.
            // Regardless of conditions met the classNames should be updated.
            const view = match.displayCoordinator as CustomPromptView
            canShow ? view._setEligibleClassNames() : view._setIneligibleClassNames()
        }

        if (!canShow) {
            return
        }

        if (!this._isEnabled) {
            this._enable()
        }

        await this._attemptToShowPrompt(match)
    }

    /**
     * Evaluates passed Prompt conditions, if met, shows the passed prompt otherwise do nothing.
     */
    protected async _attemptToShowPrompt(promptMatch: PromptMatch): Promise<void> {
        if (this.isPromptingSuppressed) {
            this.logger.verbose('Prompt not shown - prompting is suppressed')
            this.debugController._debugPromptNotShown(DebugEventReason.PROMPTING_SUPPRESSED)
            return
        }

        const isCustomPrompt = promptMatch.prompt.style === PromptStyle.PUBLISHER_CUSTOM
        if (isCustomPrompt || (await this._evaluatePromptConditions(promptMatch.prompt))) {
            if (this._abandonDisplayBecausePromptingIsSuppressed()) {
                return
            }

            await this._showMatchedPrompt(promptMatch)
        }
    }

    /**
     * Shows the passed prompt, without evaluating group/prompt conditions.
     * If {@link HtmlPrompt} creates a new {@link PromptView} and then displays PromptView, otherwise requests
     * system permissions prompt.
     *
     * **Stops** current PromptController timer/evaluations.
     *
     * **Increments** passed HtmlPrompt's view occurrences
     */
    protected async _showMatchedPrompt(promptMatch: PromptMatch): Promise<void> {
        // prevent further evaluations once a prompt has been approved for display
        this._disable()

        // ensure a display coordinator is assigned (NATIVE prompts never get one - see `_buildMatchedPrompt`)
        const match = promptMatch.displayCoordinator ? promptMatch : await this._buildMatchedPrompt(promptMatch)

        if (this._abandonDisplayBecausePromptingIsSuppressed()) {
            return
        }

        const activeView = match.displayCoordinator

        if (match.prompt.style !== PromptStyle.NATIVE) {
            // Every non-NATIVE style is guaranteed a displayCoordinator by `_buildMatchedPrompt`.
            // A prompt that renders nothing must not claim the page: counting a view it never had
            // burns its frequency cap, and holding `activeView` blocks every remaining prompt for
            // the rest of the session - so one unrenderable prompt would silence all of them.
            const outcome = activeView!._display()

            if (outcome === 'nothing-to-render') {
                // Nothing reached the page, so the page is still free. Counting a view here would
                // burn the prompt's frequency cap against an impression the visitor never saw, and
                // holding `activeView` would block every remaining prompt for the session.
                this.activeView = undefined
                this._enable()
                return
            }

            // `already-displaying` means the prompt IS on the page. Releasing it here would leave
            // a visible prompt the controller no longer tracks, and its Allow and Dismiss buttons
            // would dispatch into a controller that drops them for having no active view.
            this.activeView = activeView

            if (outcome === 'displayed') {
                await this.settingsStore._incrementViewedOccurrencesForPromptId(match.prompt.id)
            }

            return
        }

        this.activeView = activeView
        await this.settingsStore._incrementViewedOccurrencesForPromptId(match.prompt.id)

        if (this._abandonDisplayBecausePromptingIsSuppressed()) {
            return
        }

        {
            // No displayCoordinator exists for NATIVE prompts to signal "currently showing" via
            // `activeView` — track it here instead, for `_currentlyShowingPrompt`.
            this.nativePromptCurrentlyShowing = match.prompt

            // Ensure post-dismiss auto-expiration settings are set
            this.notificationPermissionService._setPermissionEventContext({
                dismissalExpirationSeconds: this._extractPromptDismissalExpirationValue(match.prompt),
            })

            await this.notificationPermissionService._requestPermission()
        }
    }

    /**
     * Ingests {@link PromptDispatchMessage} from Prompt actions and removes the current active view.
     *
     * Seeds either a {@link TrackedEventAction.PROMPT_DISMISSED}
     * or {@link TrackedEventAction.PROMPT_ACCEPTED} based on passed message.
     *
     * On {@link PromptActionType.ALLOW}: requests system permission prompt.
     *
     * On {@link PromptActionType.DISMISS}: sets subscriber status to {@link SubscriberStatus.DISMISSED} with the appropriate post dismiss
     * expiration timeframe. Also unpauses timer to listen for redisplay.
     */
    public async _processDispatchMessage(message: PromptDispatchMessage): Promise<void> {
        let activeView = this.activeView

        if (
            !activeView &&
            message.prompt_action.is_custom_prompt &&
            this.matchedPrompt?.prompt.id === message.prompt_action.id
        ) {
            // Due to the nature of custom prompts the active view is not assigned
            // unless requested directly via showPrompt. In these scenarios the
            // current matchedPrompt is used to identify the activeView based on the
            // passed prompt_action.id
            activeView = this.matchedPrompt.displayCoordinator
        }

        // Ensure an active view was identified and the dispatch invoked by the SDK
        // contains the correct matching prompt id.
        if (!activeView || !message.prompt_action.id || message.prompt_action.id !== activeView.prompt.id) {
            this.logger.debug(`Prompt not available to receive message`, message)
            return
        }

        activeView._hide()

        // Ensure post-dismiss auto-expiration settings are set
        const dismissalExpirationSeconds = this._extractPromptDismissalExpirationValue(activeView.prompt)
        this.notificationPermissionService._setPermissionEventContext({ dismissalExpirationSeconds })

        switch (message.prompt_action.type) {
            case PromptActionType.ALLOW: {
                this.logger.debug('Received Prompt Allow')
                this.eventManager._track(TrackedEventAction.PROMPT_ACCEPTED, {
                    prompt_id: message.prompt_action.id,
                })

                this.promptLifecycleCallbacks?.onPushSDKDidReceivePromptResponse?.(activeView.prompt.id, true)

                const permissions = await this.notificationPermissionService._requestPermission()
                if (activeView instanceof CustomPromptView) {
                    activeView._handlePermissionsResponse(permissions)
                }

                break
            }

            case PromptActionType.DISMISS: {
                this.logger.debug('Received Prompt Dismiss')
                this.eventManager._track(TrackedEventAction.PROMPT_DISMISSED, {
                    prompt_id: message.prompt_action.id,
                })

                if (dismissalExpirationSeconds === 0) {
                    this.logger.debug(`Setting subscriber status to NOT_DETERMINED`)
                    await this.userStore._setSubscriberStatus(SubscriberStatus.NOT_DETERMINED)
                } else {
                    this.logger.debug(`Setting subscriber status to DISMISSED for ${dismissalExpirationSeconds}s`)
                    await this.userStore._setSubscriberStatusDismissed(dismissalExpirationSeconds)
                }

                this.notificationPermissionService._resetPermissionEventContext()
                this.promptLifecycleCallbacks?.onPushSDKDidReceivePromptResponse?.(activeView.prompt.id, false)

                break
            }

            default: {
                this.logger.warn('Received unknown prompt message: ', message)
                this.debugController._debugUnknownPromptMessage(message)
            }
        }
    }

    /**
     * Seeds {@link TrackedEventAction.PROMPT_SHOWN} and passes the shown prompt id to
     * {@link PromptLifecycleCallbacks.onPushSDKDidShowPrompt}
     */
    public async _onPromptDidShow(prompt: Prompt): Promise<void> {
        this.eventManager._track(TrackedEventAction.PROMPT_SHOWN, {
            prompt_id: prompt.id,
        })

        this.promptLifecycleCallbacks?.onPushSDKDidShowPrompt?.(prompt.id)
    }

    /**
     * Sets {@link this.activePrompt} to undefined.
     */
    public async _onPromptDidHide(prompt: Prompt): Promise<void> {
        if (prompt.style === PromptStyle.PUBLISHER_CUSTOM || prompt.id !== this.activeView?.prompt.id) {
            this.logger.verbose('Hidden prompt does not match active prompt')
            return
        }

        this.activeView = undefined
    }

    /**
     * Sorts, filters and evaluates prompts for a single {@link PromptMatch}
     * that is considered eligible based on global and group conditions.
     *
     * The match is then cached and either immediately attempts to show
     * or waits for the session timer to attempt.
     */
    protected async _startMatchedPromptSelection(): Promise<void> {
        this.logger.verbose('Starting matched prompt selection')

        // Use last matched if one already exists for the current session
        let match: PromptMatch | undefined
        if (this.matchedPrompt) {
            match = this.allPrompts.find(({ prompt }) => prompt.id === this.matchedPrompt!.prompt.id)
        } else {
            const promptId = await this.settingsStore._getLastMatchedPromptId()
            if (promptId) {
                match = this.allPrompts.find(({ prompt }) => prompt.id === promptId)
            }
        }

        /**
         * If a match already exists we still need to revalidate the group conditions
         * as the {@link PromptPageConditions} may no longer be valid.
         *
         * eg: the current location now matches the excluded_page_urls deny list.
         *
         * When a re-evaluated group no longer matches we unset the match to trigger
         * a full rematch cycle.
         *
         * <b>NOTE:</b> This initial revalidation should not reselect a child prompt
         * within the group / abtest. If the group is still valid the user should still
         * be presented with their originally matched prompt.
         */
        if (match) {
            const conditionsEval = this._evaluatePromptGroupConditions(match.group)
            if (conditionsEval.isErr() || !conditionsEval.unwrapOrNull()) {
                match = undefined
            }
        }

        if (!match) {
            // begin with flattened { group, prompt } loaded
            // 1. Sort groups by priority in ascending order
            const sortedGroups = this.allPrompts.sort(({ group: groupA }, { group: groupB }) => {
                return -(groupA.priority - groupB.priority)
            })

            if (this._isTwoStepPromptsRequired) {
                this.logger.info('Browser requires 2-step prompt experience. Filtering prompts.')
            }

            // 2. Filter prompts for each group to account for possible 2-step requirements
            const filteredGroups = sortedGroups.reduce<[group: PromptGroup, prompts: Prompt[]][]>((filtered, group) => {
                const prompts = group.group.prompts.filter(
                    (prompt) => !this._isTwoStepPromptsRequired || prompt.style !== PromptStyle.NATIVE,
                )

                if (prompts.length) {
                    filtered.push([group.group, prompts])
                } else {
                    this.logger.verbose(`Removing PromptGroup ${group.group.id} - no available prompts.`, group)
                }

                return filtered
            }, [])

            // 3. Filter down to first group with satisfied conditions
            for (const [group, prompts] of filteredGroups) {
                const conditionsEval = this._evaluatePromptGroupConditions(group)
                if (conditionsEval.isErr()) {
                    this.logger.debug(`Prompt not shown due to ${conditionsEval.getErr()}`)
                    break
                }

                if (!conditionsEval.unwrapOrNull()) {
                    continue
                }

                match = {
                    group,
                    prompt: prompts.length === 1 ? prompts[0] : this._pickWeightedRandomPrompt(prompts),
                }
            }

            /**
             * `display_to_pct` is a threshold that determines if the
             * matched prompt should be displayable in current evaluation.
             * When a value < 100 is provided a roll is made and if the
             * result is within (lte) the provided threshold the match is retained.
             */
            if (match && match.group.behavior.display_to_pct !== undefined) {
                const displayPct = match.group.behavior.display_to_pct
                if (displayPct < 100) {
                    const roll = Math.floor(Math.random() * 100)
                    if (roll > displayPct) {
                        match = undefined
                    }
                }
            }
        }

        if (match && (await this._hasMetGlobalAndUserConditions(match.prompt))) {
            this.logger.debug('Eligible prompt found', match)

            this.promptLifecycleCallbacks?.onPushSDKUserIsEligibleToPrompt?.()

            // Whether attempting to show or delay - setLastMatched for follow on dismissed state actions
            await this._setLastMatchedPrompt(match)

            if (!isAutoShowPrompt(match.prompt)) {
                return
            }

            if (isSessionStartPrompt(match.prompt)) {
                await this._attemptToShowPrompt(match)
            } else {
                this.logger.verbose('Eligible prompt display delayed due to conditions', match.prompt.conditions)
            }
        } else {
            this.logger.info('No matched prompts available')

            this._disable()
            await this._clearLastMatchedPrompt()
            this.promptLifecycleCallbacks?.onPushSDKUserIsIneligibleToPrompt?.()

            if (!match) {
                // Prompts are configured for the domain but none matched this page's
                // group/page conditions (e.g. the homepage). The supported visitor had
                // no eligible prompt, so record it — otherwise the visit produces no
                // prompt decision at all. When a match existed but the user was
                // ineligible, _hasMetGlobalAndUserConditions already emitted the
                // specific reason, so this is guarded on the no-match case only.
                this.debugController._debugPromptNotShown(DebugEventReason.NO_ELIGIBLE_PROMPTS)
            }
        }
    }

    /**
     * Evaluates if a user is eligible to prompt. Once a user is determined eligible, checks if all global conditions
     * have been met.
     *
     * If {@link ShowPromptOptions.skipEligibilityCheck} is TRUE does not evaluate user eligibility or global conditions.
     */
    public async _hasMetGlobalAndUserConditions(prompt: Prompt, options?: ShowPromptOptions): Promise<boolean> {
        if (options?.skipEligibilityCheck) {
            return (
                prompt.style === PromptStyle.PUBLISHER_CUSTOM ||
                (await this.userStore._getSubscriberStatus()) !== SubscriberStatus.DENIED
            )
        }

        const userEligibleToPrompt = await this.notificationPermissionService._getIsEligibleToPrompt()
        if (userEligibleToPrompt.isErr()) {
            const reason = userEligibleToPrompt.getErr()

            const debugPromptNotShown =
                reason === DebugEventReason.USER_PERMISSION_DENIED ||
                reason === DebugEventReason.USER_PERMISSION_DISMISSED ||
                reason === DebugEventReason.USER_PERMISSION_GRANTED

            if (debugPromptNotShown) {
                this.logger.debug(`Prompt not shown due to: ${reason}`)
                this.debugController._debugPromptNotShown(reason)
            }

            return false
        }

        const globalConditionsMet = await this._evaluateGlobalConditions(prompt)
        if (globalConditionsMet.isErr()) {
            this.logger.debug(`Prompt not shown due to: ${globalConditionsMet.getErr()}`)
            return false
        }

        return true
    }

    protected _extractPromptDismissalExpirationValue(prompt: Prompt): number {
        const redisplay = prompt.behavior.post_dismiss_redisplay
        return redisplay.enabled ? redisplay.fcap.interval_seconds : Globals._defaultDismissedStatusExpirationSeconds
    }

    /**
     * Selects and returns a single {@link Prompt} from
     * the specified list using weighted random selection.
     */
    protected _pickWeightedRandomPrompt(prompts: Prompt[]) {
        const options = prompts.reduce<Prompt[]>((table, prompt) => {
            return table.concat(new Array(prompt.weight).fill(prompt))
        }, [])

        return options[Math.floor(Math.random() * options.length)]
    }

    // region View Builders

    /**
     * Attaches, if not already attached, the proper displayCoordinator
     * for the given prompt.
     *
     * NATIVE prompts never display a {@link PromptView} — {@link _showMatchedPrompt} routes them
     * straight to {@link NotificationPermissionService._requestPermission} — so no
     * displayCoordinator is built for them here; building one would be dead work.
     */
    protected async _buildMatchedPrompt(match: PromptMatch): Promise<PromptMatch> {
        if ('displayCoordinator' in match && match.displayCoordinator) {
            return match
        }

        if (match.prompt.style === PromptStyle.NATIVE) {
            return match
        }

        return {
            ...match,
            displayCoordinator:
                match.prompt.style === PromptStyle.PUBLISHER_CUSTOM
                    ? await this._buildCustomPromptView(match)
                    : this._buildPromptView(match),
        }
    }

    /**
     * Builds and returns a {@link PromptView} and runs any post-init callbacks.
     */
    protected _buildPromptView(match: PromptMatch): PromptView {
        if (match.displayCoordinator instanceof PromptView) {
            return match.displayCoordinator
        }

        const view = new PromptView(match.prompt as HtmlPrompt, match.group, this, this.debugController)

        // Run any post-initialization callbacks
        this.promptViewInitializedCallbacks.forEach((cb) => cb(view))

        return view
    }

    /**
     * Builds and returns a {@link CustomPromptView} and runs any post-init callbacks.
     */
    protected async _buildCustomPromptView(match: PromptMatch): Promise<CustomPromptView> {
        if (match.displayCoordinator instanceof CustomPromptView) {
            return match.displayCoordinator
        }

        const view = new CustomPromptView(
            match.prompt,
            match.group,
            await this._hasMetGlobalAndUserConditions(match.prompt),
            this.pushSdkConfig,
            this.userStore,
            this.eventManager,
            this,
        )

        // Run any post-initialization callbacks
        this.promptViewInitializedCallbacks.forEach((cb) => cb(view))

        return view
    }

    // endregion
    // region Evaluations
    // region Global Conditions

    /**
     * Return whether the global conditions to show a prompt have been met.
     *
     * Checks:
     * - Is a Prompt already showing?
     * - Have prompts shown exceeded the domain frequency caps settings?
     */
    protected async _evaluateGlobalConditions(prompt: Prompt): Promise<Result<boolean, string>> {
        const id = prompt.id
        // Only 1 prompt may be displayed at a time. `_currentlyShowingPrompt` covers both a
        // non-NATIVE prompt mid-display (`activeView`) and a NATIVE prompt whose browser
        // permission dialog is open (`nativePromptCurrentlyShowing`) — `activeView` alone would
        // miss the latter, since NATIVE prompts never get a displayCoordinator.
        const currentlyShowing = this._currentlyShowingPrompt
        if (currentlyShowing) {
            if (id !== undefined && currentlyShowing.id === id) {
                this.promptLifecycleCallbacks?.onPushSDKFailedToShowPrompt?.(
                    id,
                    PromptFailedToShowReason.ALREADY_SHOWING,
                )
                return Result.Err(`Prompt ${id} is already showing`)
            } else {
                this.promptLifecycleCallbacks?.onPushSDKFailedToShowPrompt?.(
                    null,
                    PromptFailedToShowReason.OTHER_PROMPT_SHOWING,
                )
                return Result.Err(`Another prompt, ${currentlyShowing.id}, is already showing.`)
            }
        }

        const promptFcap = this.domain.frequency_caps?.prompts
        if (promptFcap) {
            const withinDomainFCap = await this._evaluateDomainPromptFCap(id, promptFcap)
            if (withinDomainFCap.isErr()) {
                this.debugController._debugPromptNotShown(DebugEventReason.PROMPT_INELIGIBLE_FCAP)
                return withinDomainFCap
            }
        }

        return Result.Ok(true)
    }

    /**
     * Returns whether the prompt domain frequency caps has been exceeded or not, within the given interval.
     */
    protected async _evaluateDomainPromptFCap(promptId: number, fcap: FrequencyCap): Promise<Result<boolean, string>> {
        // ensure all expired occurrences are removed
        await this.settingsStore._dropViewedPromptOccurrencesNotInInterval(fcap.interval_seconds)
        const promptViewedOccurrences = await this.settingsStore._getViewedPromptOccurrencesCount()

        if (promptViewedOccurrences >= fcap.occurrences) {
            this.promptLifecycleCallbacks?.onPushSDKFailedToShowPrompt?.(
                promptId,
                PromptFailedToShowReason.USER_INELIGIBLE,
            )

            return Result.Err(DebugEventReason.PROMPT_INELIGIBLE_FCAP)
        }

        return Result.Ok(true)
    }

    // endregion
    // region Group Conditions

    /**
     * Return whether {@link PromptGroup}.{@link PromptGroupConditions} are met.
     *
     * If conditions do not exist for the passed group, returns TRUE
     */
    protected _evaluatePromptGroupConditions(group: PromptGroup): Result<boolean, string> {
        if (group.conditions?.display) {
            const res = this._evaluatePromptGroupDisplayConditions(group.conditions.display)
            if (!res.unwrapOrNull()) {
                return res
            }
        }

        if (group.conditions?.page) {
            return this._evaluatePromptGroupPageConditions(group.conditions.page)
        }

        return Result.Ok(true)
    }

    /**
     * Returns whether {@link PromptGroupConditions}.[display]{@link PromptDisplayConditions} are enabled, pass or disabled.
     *
     * When **pass**, evaluation will move on to the next available display type, or ignore this evaluation.
     * When **disabled**, evaluation will return Result.Err() to be handled by caller.
     */
    protected _evaluatePromptGroupDisplayConditions(conditions: PromptDisplayConditions): Result<boolean, string> {
        // Normalized before comparison. The switch compares byte-exact lowercase literals, so
        // without this a `display.mobile` of `DISABLED` or ` disabled` falls through to the
        // default and a domain that deliberately turned mobile prompting off starts prompting.
        // Absence and suppression are different answers; only absence should default open.
        switch (
            String(conditions?.[this._devicePlacement] ?? '')
                .trim()
                .toLowerCase()
        ) {
            case 'enabled':
                return Result.Ok(true)
            case 'pass':
                return Result.Ok(false)
            case 'disabled':
                return Result.Err(`platform_disabled_${this._devicePlacement}`)
            default:
                // The payload is not schema-validated, so this arrives as anything: an absent
                // placement, a null, or an unrecognised word. Returning nothing hands
                // `undefined` to the caller's `unwrapOrNull()` and throws inside its loop over
                // every group, costing the page all of its prompts rather than this one
                // condition. Eligible is the consistent answer: a group with no display
                // conditions at all is already treated as eligible, and a placement the payload
                // never expressed is the same absence one level down.
                return Result.Ok(true)
        }
    }

    /**
     * Returns whether {@link PromptGroup}.[page]{@link PromptPageConditions} are met for current web page (on load).
     * Should only be re-evaluated on next page change via timer task.
     */
    protected _evaluatePromptGroupPageConditions(conditions: PromptPageConditions): Result<boolean, string> {
        const isCurrentHrefMatch = (urlPattern: string) => {
            const pattern = urlPattern.replace(/\?/g, '\\?').replace(/\+|%20/g, '(?:\\+|%20)')
            return testSafeCaseInsensitivePattern(this._currentHref, pattern)
        }

        // URL Exclusion Check

        if (Array.isArray(conditions.excluded_page_urls) && conditions.excluded_page_urls.length > 0) {
            if (conditions.excluded_page_urls.some(isCurrentHrefMatch)) {
                return Result.Ok(false)
            }
        }

        // Meta-Keywords Inclusion Check

        if (Array.isArray(conditions.keywords) && conditions.keywords.length > 0) {
            if (!this._currentPageMetaKeywords.length) {
                return Result.Ok(false)
            }

            // normalize keywords
            const keywords = conditions.keywords.map((kw) => kw.trim().toLowerCase())

            if (!keywords.some((kw) => this._currentPageMetaKeywords.includes(kw))) {
                return Result.Ok(false)
            }
        }

        // URL Inclusion Check

        if (Array.isArray(conditions.page_urls) && conditions.page_urls.length > 0) {
            return Result.Ok(conditions.page_urls.some(isCurrentHrefMatch))
        }

        return Result.Ok(true)
    }

    // endregion
    // region Prompt Conditions

    /**
     * Evaluate each applicable prompt condition found in {@link Prompt}.[conditions]{@link PromptConditions}
     * according to their type and value.
     *
     * If no conditions exist, returns TRUE.
     */
    protected async _evaluatePromptConditions(prompt: Prompt) {
        const condition = getPromptCondition(prompt.conditions)

        if (isSessionTimeIntervalPromptCondition(condition)) {
            return this._evaluatePromptSessionTimeIntervalCondition(condition)
        }

        if (isPageViewsPromptCondition(condition)) {
            return this._evaluatePromptPageViewsCondition(condition)
        }

        if (isScrollDepthPromptCondition(condition)) {
            return this._evaluatePromptScrollDepthCondition(condition)
        }

        return true
    }

    /**
     * Return whether session page views is greater than {@link PromptVisitorConditions}.session_page_views
     */
    protected async _evaluatePromptPageViewsCondition(condition: PromptPageViewsCondition) {
        this.logger.info(`Prompt condition: Page views [at least ${condition.minPageViews} pages]`)
        const currentPageViews = await this.sessionManager.transient.store._getSessionPageViewCount()
        return currentPageViews >= (condition.minPageViews ?? 0)
    }

    /**
     * Return whether session_time_on_site or time_on_page conditions have been met. Uses {@link SessionManager} managed
     * data stores to check conditions.
     */
    protected async _evaluatePromptSessionTimeIntervalCondition(condition: PromptSessionTimeIntervalCondition) {
        switch (condition.type) {
            case PromptConditionType.SESSION_TIME_ON_SITE: {
                this.logger.info(
                    `Prompt condition: Time on site [at least ${condition.timeInterval.intervalSeconds} seconds]`,
                )
                const runtimeSeconds = await this.sessionManager.transient.store._getSessionRuntimeSeconds()
                return runtimeSeconds >= (condition.timeInterval.intervalSeconds ?? 0)
            }

            case PromptConditionType.SESSION_TIME_ON_PAGE: {
                this.logger.info(
                    `Prompt condition: Time on page [at least ${condition.timeInterval.intervalSeconds} seconds]`,
                )
                const startTime = await this.sessionManager.transient.store._getCurrentPageViewStart()
                if (!startTime) {
                    return false
                }

                const diffSeconds = Math.abs(timeIntervalFromNow(startTime.getTime())) / 1000
                return diffSeconds >= (condition.timeInterval.intervalSeconds ?? 0)
            }
        }
    }

    protected _evaluatePromptScrollDepthCondition(condition: PromptScrollDepthCondition): boolean {
        const currentMaxDepth = this.scrollDepthObserver._currentMaxDepth
        this.logger.info(`Prompt condition: Scroll depth [at least ${condition.minScrollDepthPct}%]`)

        if (!Number.isFinite(currentMaxDepth)) {
            return false // cannot compute finite depth -> withhold
        }

        return currentMaxDepth >= condition.minScrollDepthPct
    }

    // endregion
    // endregion
}
